<template>
    <div class="dk-wizard-tabs">
        <div
            class="dk-wizard-tabs__header tw-relative tw-w-full tw-shadow-[inset_0_-2px_0] tw-shadow-slate-100 lg:!tw-shadow-lg-down"
        >
            <div
                class="tw-relative lg:tw-max-w-[912px] tw-flex tw-items-stretch tw-justify-between lg:tw-mx-auto tw-overflow-x-auto tw-overflow-y-hidden"
            >
                <a
                    v-for="(tab, i) in tabs"
                    v-show="tab.isVisible"
                    :key="i"
                    :href="tab.hash"
                    class="dk-wizard-tab-control tw-relative tw-z-10 tw-block tw-flex-none tw-px-4 lg:tw-px-6 tw-py-2 lg:tw-py-3 body-sm tw-border-b-2 tw-border-solid max-lg:tw-border-slate-100 hover:tw-text-brand-primary tw-text-center lg:tw-border-b-transparent tw-text-sm tw-leading-sm"
                    :class="{
                        '!tw-border-brand-primary': tab.isActive,
                        'tw-text-base-subtle': !tab.isActive,
                        'tw-pointer-events-none tw-cursor-default tw-text-disabled': tab.disabled,
                    }"
                    :aria-controls="tab.hash"
                    :aria-selected="tab.isActive"
                    role="tab"
                    v-html="tab.header"
                >
                </a>
            </div>
        </div>
        <div :class="slotClasses">
            <slot></slot>
        </div>
    </div>
</template>

<script>
/**
 * A component for displaying a tab system.
 *
 * @version 4.0.0
 * @example ./examples/DkTabs.md
 */
export default {
    name: 'WizardTabs',
    props: {
        /*
         * Selected color object to understand
         * where the default or a color picker image
         * is displayed (the images have different layouts)
         */
        activeColor: {
            type: Object,
            default: () => ({}),
        },
        hasDealerSelection: {
            type: Boolean,
            default: false,
        },
        selectedDealer: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            tabs: [],
            activeTabHash: '',
            lastActiveTabHash: '',
            selectedTabIndex: 0,
            isGoingBackToLeadFormIsAllowed: true,
        };
    },
    computed: {
        opts() {
            return {
                useUrlFragment: true,
                defaultTabHash: null,
                spaceBetween: 20,
                lines: true,
            };
        },
        slotClasses() {
            if (this.hasDealerSelection) {
                const isFirstTabWithDefaultImage = this.selectedTabIndex === 0 && !this.activeColor;
                const isDealerOrFormTabWithDefaultImage =
                    (this.selectedTabIndex === 2 || this.selectedTabIndex === 3) && !this.activeColor;
                const isColorTabOrAnyOtherTabWithDefaultImage =
                    this.selectedTabIndex === 1 || (this.selectedTabIndex !== 1 && this.activeColor);

                if (isFirstTabWithDefaultImage) {
                    return 'tw-mt-2';
                }

                if (isDealerOrFormTabWithDefaultImage) {
                    return 'tw-mt-2 lg:tw-mt-0';
                }

                if (isColorTabOrAnyOtherTabWithDefaultImage) {
                    return 'lg:tw-mt-2';
                }
            }

            return '';
        },
    },
    watch: {
        // Moved the call for `handleTabSelection()`
        // from `mounted()` to this watcher
        // because in some cases `mounted`
        // was triggered before the `v-for`
        // for the tabs, therefore tabs === []
        // when `handleTabSelection()` was called
        // and the first tab was not selected.
        tabs(tabsUpdated) {
            if (tabsUpdated && tabsUpdated.length) {
                this.handleTabSelection();
            }
        },
        selectedDealer(newDealer) {
            if (!this.hasDealerSelection) {
                return;
            }

            if (newDealer && !this.isGoingBackToLeadFormIsAllowed) {
                this.isGoingBackToLeadFormIsAllowed = true;
            } else if (!newDealer && this.isGoingBackToLeadFormIsAllowed) {
                this.isGoingBackToLeadFormIsAllowed = false;
            }
        },
    },
    created() {
        this.tabs = this.$children;
        // to allow the navigation back and forward in browser
        // on configurator wizard
        window.addEventListener('popstate', (e) => this.handleNavigation(e));
    },
    beforeDestroy() {
        window.removeEventListener('popstate', (e) => this.handleNavigation(e));
    },
    methods: {
        selectTab(selectedTabHash, event) {
            if (event && !this.opts.useUrlFragment) {
                event.preventDefault();
            }

            const selectedTab = this.findTab(selectedTabHash);

            if (!selectedTab) {
                return;
            }

            if (selectedTab.disabled) {
                return event && event.preventDefault();
            }

            if (this.lastActiveTabHash === selectedTab.hash) {
                /**
                 * This event is fired when the same tab has been clicked multiple times
                 * @event re-clicked
                 * @type {Object}
                 */
                return event && this.$emit('re-clicked', this.eventPayload(selectedTab, event));
            }

            for (const tab of this.tabs) {
                tab.isActive = tab.hash === selectedTab.hash;
            }

            this.selectedTabIndex = this.getTabIndex(selectedTabHash);
            this.lastActiveTabHash = this.activeTabHash = selectedTab.hash;
            /**
             * This event is fired when a tab has been clicked
             * @event change
             * @type {Object}
             */
            event && this.$emit('change', this.eventPayload(selectedTab, event));
        },
        handleTabSelection() {
            if (this.findTab(window.location.hash)) {
                return this.selectTab(window.location.hash);
            }

            if (this.opts.defaultTabHash && this.findTab(`#${this.opts.defaultTabHash}`)) {
                return this.selectTab(`#${this.opts.defaultTabHash}`);
            }

            if (this.tabs.length) {
                this.selectTab(this.tabs[0].hash);
            }
        },
        eventPayload(tab, event) {
            return {
                index: this.selectedTabIndex,
                tab,
                event,
            };
        },
        isLastTab(index) {
            return !(index < this.tabs.length - 1);
        },
        findTab(hash) {
            return this.tabs.find((tab) => tab.hash === hash);
        },
        getTabIndex(hash) {
            return this.tabs.indexOf(this.findTab(hash));
        },
        handleNavigation(e) {
            // if the dealer information is being cleaned,
            // the user can't go back to the lead form via browser navigation
            const isLeadFormTab = e.target.location.hash === '#configuration-wizard__price';
            if (!this.isGoingBackToLeadFormIsAllowed && isLeadFormTab) {
                window.location.hash = this.activeTabHash;
                this.selectTab(this.activeTabHash, e);
            } else {
                this.selectTab(e.target.location.hash, e);
            }
        },
    },
};
</script>

<style scoped>
.dk-tab-control {
    -webkit-tap-highlight-color: transparent;
}
</style>
